import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Paprika, PaprikaLayout } from '../../../../styles/theme';
import { SHEET_DURATION } from '../constants';

interface Props {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxHeight?: number | string;
  snapPoint?: number; // fixed height, otherwise sizes to content
  /**
   * False when the children bring their own virtualized list: a FlatList
   * nested in this ScrollView renders every row and loses windowing. Pair it
   * with `snapPoint`, since the children then fill a fixed height.
   */
  scrollable?: boolean;
}

export function BottomSheet({ visible, onClose, children, maxHeight = '90%', snapPoint, scrollable = true }: Props) {
  const translateY = useRef(new Animated.Value(800)).current;
  const scrimOpacity = useRef(new Animated.Value(0)).current;

  // Respect prefers-reduced-motion by skipping animation
  const prefersReduced =
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const duration = prefersReduced ? 0 : SHEET_DURATION;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(scrimOpacity, {
          toValue: 1,
          duration,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 800,
          duration: prefersReduced ? 0 : 220,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(scrimOpacity, {
          toValue: 0,
          duration: prefersReduced ? 0 : 180,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.root}>
        <Animated.View style={[styles.scrim, { opacity: scrimOpacity }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        <Animated.View
          testID="bottom-sheet"
          style={[
            styles.sheet,
            snapPoint ? { height: snapPoint } : { maxHeight: maxHeight as any },
            { transform: [{ translateY }] },
          ]}
        >
          <View style={styles.handle} />
          {scrollable ? (
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {children}
            </ScrollView>
          ) : (
            <View style={styles.scroll}>{children}</View>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(27, 28, 26, 0.48)',
  },
  sheet: {
    // Matches the planner's content column, so a sheet never gets wider than
    // the screen behind it (#632). Below 720 this is a no-op and the sheet is
    // the full-bleed drawer it has always been; above it, the sheet reads as a
    // centred card with scrim either side.
    width: '100%',
    maxWidth: PaprikaLayout.contentMax,
    alignSelf: 'center',
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: 'rgba(82, 68, 60, 0.16)',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 24,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: Paprika.border,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 4,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
});
